import { useCallback, useRef } from 'react';
import { clientPointToImageCoords, imageCoordsToContainPercent } from '../utils/photorealistic/canvasImageCoords';

const MIN_PLACEMENT_WIDTH_PX = 32;

function placementHeightPx(item) {
  const w = Number(item?.width) || MIN_PLACEMENT_WIDTH_PX;
  const ar = Number(item?.aspectRatio) > 0 ? Number(item.aspectRatio) : 1;
  return w / ar;
}

/**
 * Renders curated model thumbnails positioned in image pixel space over the canvas.
 */
export default function PlacedCuratedModelsOverlay({
  containerRef,
  logicalWidth,
  logicalHeight,
  placements = [],
  selectedPlacementId = null,
  onSelectPlacement,
  onMovePlacement,
  onResizePlacement,
  onRemovePlacement,
  onOpenTransformModelsPanel,
  disabled = false,
  minPlacementWidth = MIN_PLACEMENT_WIDTH_PX,
  maxPlacementWidth = null,
}) {
  const interactionRef = useRef({
    mode: null,
    placementId: null,
    pointerId: null,
    grabOffsetX: 0,
    grabOffsetY: 0,
  });

  const maxWidthPx =
    maxPlacementWidth != null && Number(maxPlacementWidth) > 0
      ? Number(maxPlacementWidth)
      : logicalWidth > 0
        ? logicalWidth * 0.6
        : 800;

  const clampWidth = useCallback(
    (w) => Math.min(maxWidthPx, Math.max(minPlacementWidth, w)),
    [maxWidthPx, minPlacementWidth],
  );

  const endInteraction = useCallback(() => {
    interactionRef.current = {
      mode: null,
      placementId: null,
      pointerId: null,
      grabOffsetX: 0,
      grabOffsetY: 0,
    };
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      const state = interactionRef.current;
      if (!state.mode || state.placementId == null) return;
      if (state.pointerId != null && event.pointerId !== state.pointerId) return;

      const container = containerRef?.current;
      if (!container) return;

      const pt = clientPointToImageCoords(
        event.clientX,
        event.clientY,
        container,
        logicalWidth,
        logicalHeight,
      );
      if (!pt) return;

      if (state.mode === 'move' && onMovePlacement) {
        onMovePlacement(
          state.placementId,
          pt.x - state.grabOffsetX,
          pt.y - state.grabOffsetY,
        );
        return;
      }

      if (state.mode === 'resize' && onResizePlacement) {
        const placement = placements.find((p) => p.placementId === state.placementId);
        if (!placement) return;
        const ar = Number(placement.aspectRatio) > 0 ? Number(placement.aspectRatio) : 1;
        const halfW = Math.abs(pt.x - placement.x);
        const halfH = Math.abs(pt.y - placement.y);
        const half = Math.max(halfW, halfH * ar);
        onResizePlacement(state.placementId, clampWidth(half * 2));
      }
    },
    [
      containerRef,
      logicalWidth,
      logicalHeight,
      onMovePlacement,
      onResizePlacement,
      placements,
      clampWidth,
    ],
  );

  const startMove = useCallback(
    (event, item) => {
      if (disabled) return;
      const container = containerRef?.current;
      const grabPt =
        container &&
        clientPointToImageCoords(
          event.clientX,
          event.clientY,
          container,
          logicalWidth,
          logicalHeight,
        );
      if (!grabPt) return;
      onSelectPlacement?.(item.placementId);
      interactionRef.current = {
        mode: 'move',
        placementId: item.placementId,
        pointerId: event.pointerId,
        grabOffsetX: grabPt.x - item.x,
        grabOffsetY: grabPt.y - item.y,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [disabled, containerRef, logicalWidth, logicalHeight, onSelectPlacement],
  );

  const startResize = useCallback(
    (event, item) => {
      if (disabled || !onResizePlacement || selectedPlacementId !== item.placementId) return;
      event.stopPropagation();
      event.preventDefault();
      interactionRef.current = {
        mode: 'resize',
        placementId: item.placementId,
        pointerId: event.pointerId,
        grabOffsetX: 0,
        grabOffsetY: 0,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [disabled, onResizePlacement, selectedPlacementId],
  );

  const handleRotateClick = useCallback(
    (event, item) => {
      event.stopPropagation();
      event.preventDefault();
      onSelectPlacement?.(item.placementId);
      onOpenTransformModelsPanel?.();
    },
    [onSelectPlacement, onOpenTransformModelsPanel],
  );

  if (!(logicalWidth > 0) || !(logicalHeight > 0) || placements.length === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[15] overflow-hidden rounded"
      onPointerMove={handlePointerMove}
      onPointerUp={endInteraction}
      onPointerCancel={endInteraction}
    >
      {placements.map((item) => {
        const { leftPct, topPct } = imageCoordsToContainPercent(
          item.x,
          item.y,
          logicalWidth,
          logicalHeight,
        );
        const widthPct = (item.width / logicalWidth) * 100;
        const heightPx = placementHeightPx(item);
        const heightPct = (heightPx / logicalHeight) * 100;
        const isSelected = selectedPlacementId === item.placementId;
        return (
          <div
            key={item.placementId}
            data-placed-curated-model
            role="button"
            tabIndex={0}
            aria-label={`Placed model ${item.name || 'Model'}. Drag to move.`}
            className={`pointer-events-auto absolute touch-none select-none border-2 ${
              isSelected ? 'border-primary-300' : 'border-transparent'
            } ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
            style={{
              left: `${leftPct}%`,
              top: `${topPct}%`,
              width: `${widthPct}%`,
              height: `${heightPct}%`,
              transform: 'translate(-50%, -50%)',
            }}
            onPointerDown={(e) => {
              if (disabled) return;
              e.stopPropagation();
              e.preventDefault();
              startMove(e, item);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Delete' || e.key === 'Backspace') {
                onRemovePlacement?.(item.placementId);
              }
            }}
          >
            <div
              className="relative h-full w-full overflow-visible"
              style={{
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }}
            >
              {item.thumbSrc ? (
                <img
                  src={item.thumbSrc}
                  alt={item.name || 'Model'}
                  className="h-full w-full object-contain pointer-events-none"
                  draggable={false}
                  referrerPolicy="no-referrer"
                />
              ) : null}
              {isSelected && !disabled ? (
                <div className="absolute top-0.5 right-0.5 z-10 flex items-center gap-0.5">
                  {onOpenTransformModelsPanel ? (
                    <button
                      type="button"
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-900/75 text-white transition-colors hover:bg-gray-900"
                      title="Rotate model"
                      aria-label="Rotate model"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => handleRotateClick(e, item)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                        <path d="M21 3v5h-5" />
                      </svg>
                    </button>
                  ) : null}
                  {onRemovePlacement ? (
                    <button
                      type="button"
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-900/75 text-white hover:bg-gray-900"
                      title="Remove from canvas"
                      aria-label="Remove from canvas"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemovePlacement(item.placementId);
                      }}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              ) : null}
              {isSelected && onResizePlacement && !disabled ? (
                <>
                  <span
                    className="absolute -bottom-1 -right-1 z-10 h-3.5 w-3.5 rounded-sm border-2 border-white bg-primary-300 shadow cursor-nwse-resize pointer-events-auto"
                    title="Drag to resize"
                    aria-label="Resize corner"
                    onPointerDown={(e) => startResize(e, item)}
                  />
                  <span
                    className="absolute -bottom-1 -left-1 z-10 h-3.5 w-3.5 rounded-sm border-2 border-white bg-primary-300 shadow cursor-nesw-resize pointer-events-auto"
                    title="Drag to resize"
                    aria-label="Resize corner"
                    onPointerDown={(e) => startResize(e, item)}
                  />
                  <span
                    className="absolute -top-1 -right-1 z-10 h-3.5 w-3.5 rounded-sm border-2 border-white bg-primary-300 shadow cursor-nesw-resize pointer-events-auto"
                    title="Drag to resize"
                    aria-label="Resize corner"
                    onPointerDown={(e) => startResize(e, item)}
                  />
                  <span
                    className="absolute -top-1 -left-1 z-10 h-3.5 w-3.5 rounded-sm border-2 border-white bg-primary-300 shadow cursor-nwse-resize pointer-events-auto"
                    title="Drag to resize"
                    aria-label="Resize corner"
                    onPointerDown={(e) => startResize(e, item)}
                  />
                </>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
